import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { PrimaryButton, SecondaryButton } from '../../../components/atoms/Button';
import { TimeField } from '../../Booking/Details';
import {
  IAttendance,
  IAttendancePunch,
  UpdateAttendanceRecord,
} from '../../../services/technician';
import { AttendanceStatusChip } from './AttendanceStatusChip';
import { AttendanceAuditLog } from './AttendanceAuditLog';
import {
  CHECKOUT_WINDOW_LABEL,
  LATE_CUTOFF_LABEL,
} from '../../../utils/attendancePolicy';

export type AttendanceEditRecord = {
  type?: IAttendance['type'];
  status?: string;
  description?: string;
  checkInTime?: string | null;
  checkOutTime?: string | null;
  computedStatus?: string | null;
  firstCheckIn?: string | null;
  lastCheckOut?: string | null;
  workedMinutes?: number | null;
  punches?: IAttendancePunch[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  technicianId: string;
  date: Dayjs | null;
  record?: AttendanceEditRecord | null;
  onSaved: () => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
};

/** Admin-settable base types. LATE_CHECK_IN / HALF_DAY are computed-only —
 *  the policy engine derives them from punches; they are never set by hand. */
type SettableAttendanceType = 'PRESENT' | 'ABSENT' | 'LEAVE' | 'HOLIDAY';

const ATTENDANCE_TYPES: SettableAttendanceType[] = [
  'PRESENT',
  'ABSENT',
  'LEAVE',
  'HOLIDAY',
];

const formatDateDDMMYYYY = (date: Dayjs): string => date.format('DD-MM-YYYY');

const toTimeValue = (value?: string | null) =>
  value ? dayjs(value).format('HH:mm') : '';

const parseTimeParts = (time: string) => {
  const parts = time.split(':');
  if (parts.length !== 2) return null;

  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return { hours, minutes };
};

const resolveInitialType = (
  record?: AttendanceEditRecord | null,
): SettableAttendanceType => {
  // Computed statuses collapse to their settable base (they carry punches).
  if (record?.type === 'LATE_CHECK_IN' || record?.type === 'HALF_DAY') {
    return 'PRESENT';
  }

  if (
    record?.type &&
    ATTENDANCE_TYPES.includes(record.type as SettableAttendanceType)
  ) {
    return record.type as SettableAttendanceType;
  }

  const status = record?.status?.toUpperCase();
  if (status === 'LATE_CHECK_IN' || status === 'HALF_DAY') {
    return 'PRESENT';
  }
  if (status && ATTENDANCE_TYPES.includes(status as SettableAttendanceType)) {
    return status as SettableAttendanceType;
  }

  if (record?.checkInTime || record?.checkOutTime) {
    return 'PRESENT';
  }

  if (status === 'ABSENT') {
    return 'ABSENT';
  }

  return 'PRESENT';
};

export const EditAttendanceDialog: React.FC<Props> = ({
  open,
  onClose,
  technicianId,
  date,
  record,
  onSaved,
  onError,
  onSuccess,
}) => {
  const [type, setType] = useState<SettableAttendanceType>('PRESENT');
  const [description, setDescription] = useState('');
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [hadCheckout, setHadCheckout] = useState(false);

  useEffect(() => {
    if (!open || !date) return;

    setType(resolveInitialType(record));
    setDescription(record?.description?.trim() || '');
    setCheckInTime(toTimeValue(record?.firstCheckIn ?? record?.checkInTime));
    setCheckOutTime(toTimeValue(record?.lastCheckOut ?? record?.checkOutTime));
    setHadCheckout(Boolean(record?.lastCheckOut ?? record?.checkOutTime));
  }, [open, date, record]);

  const handleSave = async () => {
    if (!date) return;

    setLoading(true);

    try {
      const payload: Parameters<typeof UpdateAttendanceRecord>[0] = {
        technicianId,
        date: formatDateDDMMYYYY(date),
        type,
        description,
      };

      if (type === 'PRESENT') {
        if (checkInTime) {
          const checkInParts = parseTimeParts(checkInTime);
          if (!checkInParts) {
            onError('Invalid check-in time');
            return;
          }
          payload.checkInHour = checkInParts.hours;
          payload.checkInMinute = checkInParts.minutes;
        }

        if (checkOutTime) {
          const checkoutParts = parseTimeParts(checkOutTime);
          if (!checkoutParts) {
            onError('Invalid check-out time');
            return;
          }
          payload.checkoutHour = checkoutParts.hours;
          payload.checkoutMinute = checkoutParts.minutes;
        } else if (hadCheckout) {
          payload.clearCheckout = true;
        }
      }

      await UpdateAttendanceRecord(payload);
      onSuccess(
        record?.type || record?.checkInTime || record?.status
          ? 'Attendance updated successfully!'
          : 'Attendance saved successfully!',
      );
      onSaved();
      onClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save attendance';
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          backgroundColor: '#181818',
          color: '#fff',
          maxHeight: '80vh',
          overflowY: 'auto',
          borderRadius: 3,
          border: '1px solid #333',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '1.125rem',
          fontWeight: 600,
          color: '#f5f5f5',
          backgroundColor: '#181818',
          pb: 1,
        }}
      >
        {record?.type || record?.checkInTime || record?.status
          ? 'Edit Attendance'
          : 'Mark Attendance'}{' '}
        — {date?.format('DD MMM YYYY')}
      </DialogTitle>

      <Divider sx={{ borderColor: '#333' }} />

      <DialogContent sx={{ backgroundColor: '#181818', pt: 2.5 }}>
        {record?.computedStatus && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AttendanceStatusChip status={record.computedStatus} withHint />
            <Typography variant="caption" color="#6b7280">
              Computed by policy — recalculates after save
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="#9ca3af" gutterBottom>
          Attendance Type
        </Typography>

        <TextField
          select
          value={type}
          onChange={(event) =>
            setType(event.target.value as SettableAttendanceType)
          }
          SelectProps={{ native: true }}
          fullWidth
          size="small"
          disabled={loading}
        >
          <option value="PRESENT">Present</option>
          <option value="ABSENT">Absent</option>
          <option value="LEAVE">Leave</option>
          <option value="HOLIDAY">Holiday</option>
        </TextField>

        {type === 'PRESENT' && (
          <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="#9ca3af" gutterBottom>
                First Check-in
              </Typography>
              <TimeField
                type="time"
                value={checkInTime}
                onChange={(event) => setCheckInTime(event.target.value)}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ step: 60 }}
              />
              <Typography
                variant="caption"
                color="#6b7280"
                sx={{ mt: 0.75, display: 'block' }}
              >
                Late after {LATE_CUTOFF_LABEL}.
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="#9ca3af" gutterBottom>
                Last Check-out
              </Typography>
              <TimeField
                type="time"
                value={checkOutTime}
                onChange={(event) => setCheckOutTime(event.target.value)}
                fullWidth
                size="small"
                disabled={loading}
                inputProps={{ step: 60 }}
              />
              <Typography
                variant="caption"
                color="#6b7280"
                sx={{ mt: 0.75, display: 'block' }}
              >
                Full-day window {CHECKOUT_WINDOW_LABEL}. Leave empty to remove
                checkout for this day.
              </Typography>
            </Box>

            {Boolean(record?.punches?.length) && (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid #2a2a2a',
                  backgroundColor: '#141414',
                }}
              >
                <Typography
                  variant="caption"
                  color="#d1d5db"
                  sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}
                >
                  Punch log
                </Typography>
                {record!.punches!.map((punch, index) => (
                  <Typography
                    key={`${punch.kind}-${punch.at}-${index}`}
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: punch.kind === 'IN' ? '#22C55E' : '#EF4444',
                      fontSize: '0.72rem',
                    }}
                  >
                    {punch.kind === 'IN' ? 'IN ' : 'OUT'} ·{' '}
                    {dayjs(punch.at).format('DD MMM, hh:mm A')} ·{' '}
                    {punch.source || 'TECH_APP'}
                    {punch.note ? ` — ${punch.note}` : ''}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}

        <Typography variant="body2" color="#9ca3af" mt={2.5} gutterBottom>
          Description (optional)
        </Typography>

        <TextField
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          multiline
          rows={3}
          fullWidth
          size="small"
          disabled={loading}
        />

        <Accordion
          disableGutters
          TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
          sx={{
            mt: 2.5,
            backgroundColor: '#141414',
            border: '1px solid #2a2a2a',
            borderRadius: '8px !important',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#9ca3af' }} />}>
            <Typography variant="body2" color="#d1d5db" fontWeight={600}>
              Change history
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {date && (
              <AttendanceAuditLog
                technicianId={technicianId}
                date={formatDateDDMMYYYY(date)}
              />
            )}
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: '#181818', px: 3, pb: 2.5 }}>
        <SecondaryButton
          title="Cancel"
          onClick={onClose}
          disabled={loading}
        />
        <PrimaryButton
          title={loading ? 'Saving...' : 'Save'}
          onClick={handleSave}
          disabled={loading}
        />
      </DialogActions>
    </Dialog>
  );
};
